import {
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Box,
  Button,
  Icon,
  Card,
  Flex,
  Spinner,
  HStack,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import { Link as RouterLink } from 'react-router-dom'
import { ArrowRight, Star, MapTrifold, BookOpen } from '@phosphor-icons/react'
import { useFeaturedSites } from '../../hooks/useFeaturedSites'
import { useTexture } from '../../context/EnhancedTextureContext';
import FeaturedSiteCard from '../FeaturedSiteCard'

const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(10px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const floatSlow = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0); }
`;

export function NotableDiscoveriesSection() {
  const { data: featuredSites, loading: featuredLoading } = useFeaturedSites();
  const { isTextureEnabled } = useTexture();

  return (
    <>
      <Box 
        as="section" 
        position="relative"
        bg={useColorModeValue('bg.default', 'bg.default')}
        py={4}
        overflow="hidden" // Ensure pseudo-elements are contained
        _before={{ // Leather texture - moved to outer Box
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: isTextureEnabled ? "url('/textures/leather.png')" : 'none',
          backgroundRepeat: 'repeat',
          backgroundSize: 'auto',
          opacity: isTextureEnabled ? useColorModeValue(0.1, 0.35) : 0,
          mixBlendMode: 'multiply', // Consider changing this if leather is too dark
          pointerEvents: 'none',
          zIndex: 0, // Behind Container, above Box's own bg
          transition: 'opacity 0.3s ease-in-out',
        }}
        _after={{ // Linen texture - moved to outer Box
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: isTextureEnabled ? "url('/textures/linen.png')" : 'none',
          backgroundRepeat: 'repeat',
          backgroundSize: 'auto',
          opacity: isTextureEnabled ? useColorModeValue(0.15, 0) : 0,
          mixBlendMode: 'overlay',
          pointerEvents: 'none',
          zIndex: 0, // Behind Container, above Box's own bg
          display: useColorModeValue('block', 'none'),
          transition: 'opacity 0.3s ease-in-out',
        }}
      >
        {/* Map compass watermark - should be visually under Container but potentially over new _before/_after */}
        {/* To ensure it's distinct, if it was meant to be under textures, its zIndex might need adjustment or textures opacity carefully managed */}
        {/* For now, keeping its zIndex at 0, same level as new pseudo-elements. */}
        <Box
          position="absolute" 
          right="5%" 
          bottom="10%" 
          width="300px" 
          height="300px" 
          bgImage="url('/textures/compass-rose.png')" 
          bgPosition="center" 
          bgRepeat="no-repeat" 
          bgSize="contain" 
          opacity="0.04" // Very subtle
          zIndex={0} // Same level as Box's pseudo-elements, Container will be above
          display={{ base: 'none', lg: 'block' }}
        />
        <Container
          variant="journal"
          maxW="container.xl"
          py={{ base: 16, md: 20 }}
          position="relative"
          bg={useColorModeValue("bg.surface", "bg.surface._dark")} 
          boxShadow="lg" 
          borderRadius="md" 
          zIndex={1} // Ensure container is above the Box's pseudo-elements and compass watermark
          // _before and _after pseudo-elements removed from here
        >
          <Box
            position="absolute"
            top="20px"
            left={{ base: '20px', md: '40px' }}
            width="50px"
            height="50px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            opacity={0.6}
            animation={`${floatSlow} 6s infinite ease-in-out`}
            sx={{
              '@media(prefers-reduced-motion: reduce)': {
                animation: 'none',
              }
            }}
          >
            <Icon as={MapTrifold} boxSize={8} color="border.heavy" />
          </Box>
          
          <VStack spacing={16}>
            <VStack spacing={6} textAlign="center" position="relative">
              <HStack 
                position="absolute"
                top="-40px"
                left={{ base: '10px', md: '0px' }}
                spacing={1}
                alignItems="center"
              >
                <Icon as={BookOpen} boxSize={4} color="text.muted" opacity={0.7} />
                <Box
                  fontFamily="handwritten"
                  fontSize="sm"
                  color="text.muted"
                  fontWeight="medium"
                  borderBottom="1px dashed"
                  borderColor="border.light"
                  paddingBottom="1px"
                >
                  Journal Page 3
                </Box>
              </HStack>

              <Box
                position="relative"
                width="fit-content"
                paddingX={8}
                paddingY={2}
                _before={{
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  border: '1px solid',
                  borderColor: 'border.light',
                  opacity: 0.4,
                  zIndex: 0,
                }}
                _after={{
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: '-40px',
                  width: '30px',
                  height: '1px',
                  bg: 'border.light',
                  display: { base: 'none', md: 'block' }
                }}
                sx={{
                  '&:after:nth-of-type(2)': {
                    left: 'auto',
                    right: '-40px',
                  }
                }}
              >
                <Heading
                  variant="journal"
                  size="2xl"
                  position="relative"
                  fontFamily="heading"
                  zIndex={1}
                  textAlign="center"
                >
                  Notable Discoveries
                </Heading>
              </Box>
              
              <HStack spacing={2} justifyContent="center" mb={2} wrap="wrap">
                <Badge variant="journal">Ancient</Badge>
                <Badge variant="journal">Mysterious</Badge>
                <Badge variant="journal">Fascinating</Badge>
              </HStack>

              <Text
                fontSize="xl"
                maxW="3xl"
                color="text.muted"
                position="relative"
                fontStyle="italic"
                animation={`${fadeIn} 0.8s forwards`}
                sx={{
                  '@media(prefers-reduced-motion: reduce)': {
                    animation: 'none',
                  }
                }}
              >
                Explore some of the most fascinating megalithic sites from around the world.
                
                <Box
                  position="absolute"
                  top={{ base: '-15px', lg: '-25px' }}
                  right={{ base: '10px', md: '-80px' }}
                  fontFamily="handwritten"
                  fontSize={{ base: 'sm', lg: 'md' }}
                  color="interactive.primary"
                  transform="rotate(3deg)"
                  display={{ base: 'none', lg: 'block' }}
                  opacity={0.9}
                  padding="2px 8px"
                  borderRadius="md"
                  bg={useColorModeValue('rgba(255,255,255,0.5)', 'rgba(40,30,20,0.3)')}
                  boxShadow="sm"
                  border="1px dashed"
                  borderColor="border.light"
                  _before={{
                    content: '""',
                    position: 'absolute',
                    bottom: '-10px',
                    left: '20px',
                    width: '30px',
                    height: '1px',
                    borderBottom: '1px dashed',
                    borderColor: 'border.light',
                    transform: 'rotate(-45deg)'
                  }}
                >
                  Featured locations
                </Box>
              </Text>
            </VStack>

            <Box 
              position="relative" 
              width="100%"
              pb={8}
              _before={{
                content: '""',
                position: 'absolute',
                top: '0',
                left: '0',
                right: '0',
                bottom: '0',
                bgImage: 'repeating-linear-gradient(0deg, transparent, transparent 31px, rgba(150,150,150,0.1) 31px, rgba(150,150,150,0.1) 32px)',
                opacity: 0.3,
                pointerEvents: 'none',
                zIndex: 0
              }}
              _after={{
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: { base: '40px', md: '60px' },
                height: { base: '40px', md: '60px' },
                bg: 'transparent',
                borderLeft: '1px solid',
                borderBottom: '1px solid',
                borderColor: 'border.light',
                opacity: 0.3,
                zIndex: 0
              }}
            >
              <Box
                position="absolute"
                top="-15px"
                left="50%"
                transform="translateX(-50%)"
                width="80px"
                height="30px"
                borderRadius="3px"
                bg={useColorModeValue("rgba(193, 148, 52, 0.2)", "rgba(214, 173, 96, 0.2)")}
                boxShadow="sm"
                display={{ base: 'none', md: 'block' }}
                zIndex={2}
              />
              
              <SimpleGrid
                columns={{ base: 1, md: 3 }}
                spacing={8}
                w="full"
                position="relative"
                pt={4}
                px={{ base: 2, md: 6 }}
                zIndex={1}
              >
              {featuredLoading ? (
                <Flex justify="center" w="full" p={4} gridColumn={{ base: '1', md: '1 / span 3' }}>
                  <Spinner color="interactive.primary" />
                </Flex>
              ) : featuredSites && featuredSites.length > 0 ? (
                featuredSites.map((site) => <FeaturedSiteCard key={site.id} site={site} />)
              ) : (
                <Card 
                  variant="journal" 
                  p={8} 
                  w="full" 
                  gridColumn={{ base: '1', md: '1 / span 3' }}
                  alignItems="center"
                  boxShadow="md"
                  bg={useColorModeValue('bg.surface', 'bg.surface')}
                  borderWidth="1px"
                  borderStyle="dashed"
                  borderColor="border.default"
                >
                  <Icon as={Star} weight="duotone" fontSize="4xl" color="accent.highlight" mb={4} />
                  <Text fontSize="xl" fontWeight="medium" mb={2}>
                    No Featured Sites Yet
                  </Text>
                  <Text color="text.muted" textAlign="center" mb={4}>
                    Admins can feature sites by clicking the star button on any site's profile page.
                  </Text>
                  <Button
                    as={RouterLink}
                    to="/map"
                    size="md"
                    variant="secondary"
                    rightIcon={<Icon as={ArrowRight} weight="bold" />}
                  >
                    Explore All Sites
                  </Button>
                </Card>
              )}
              </SimpleGrid>
            </Box>

            <Box 
              width="100%" 
              textAlign="center"
              position="relative"
              _before={{
                content: '""',
                position: 'absolute',
                top: '-20px',
                left: '25%',
                right: '25%',
                height: '1px',
                bgGradient: 'linear(to-r, transparent, border.default, transparent)',
                opacity: 0.4,
              }}
            >
              <Button
                as={RouterLink}
                to="/search"
                size="lg"
                variant="primary"
                rightIcon={<Icon as={ArrowRight} weight="bold" />}
                position="relative"
                boxShadow="md"
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "lg"
                }}
                transition="all 0.3s"
              >
                Explore All Sites
              </Button>
            </Box>
          </VStack>
        </Container>
      </Box>
    </>
  );
}
